import type {
  UploadFile,
  UploadState,
} from '@pflegenavi/shared-frontend/platform';
import {
  makeApiProvider,
  useApiQuery,
  useUploadFile,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useCallback } from 'react';
import type { IServiceProviderReceiptUploadPhoenixApi } from './api';
import { ServiceProviderReceiptUploadPhoenixApi } from './api';
import type { UseQueryResult } from '@tanstack/react-query';
import { replaceEqualDeep } from '@tanstack/react-query';
import isEqual from 'lodash.isequal';

const ApiContext = createContext<
  IServiceProviderReceiptUploadPhoenixApi | undefined
>(undefined);

const {
  useApi: useServiceProviderReceiptUploadPhoenixApi,
  ApiProvider: ServiceProviderReceiptUploadPhoenixApiProvider,
} = makeApiProvider({
  name: 'ServiceProviderReceiptUploadPhoenix',
  ApiContext,
  newApi: (tenantId, auth, apiUrl) =>
    new ServiceProviderReceiptUploadPhoenixApi(tenantId, auth, apiUrl),
});

export {
  useServiceProviderReceiptUploadPhoenixApi,
  ServiceProviderReceiptUploadPhoenixApiProvider,
  ApiContext as ServiceProviderReceiptUploadPhoenixApiContext,
};

export type {
  IServiceProviderReceiptUploadPhoenixApi,
  GetUploadUrlBody,
  GetUploadUrlResult,
  GetReceiptUrlsBody,
  GetReceiptUrlsResult,
} from './api';

/**
 * Hook factory that returns a function to fetch receipt images with a specific tenant (Phoenix version)
 *
 * @param tenant - The tenant identifier
 * @returns Function that accepts receipt image IDs and returns a query result
 */
export const useGetReceiptImagesWithTenantPhoenix = ({
  tenant,
}: {
  tenant: string;
}): ((receiptImageIds?: string[]) => UseQueryResult<
  {
    [p: string]: string | undefined;
  },
  unknown
>) => {
  return (receiptImageIds: string[] | undefined) =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useReceiptImagesPhoenix(tenant, receiptImageIds);
};

const getReceiptImagesKey = (id?: string) => [
  'receipt-images-multi-phoenix',
  id,
];

/**
 * Query hook to fetch presigned download URLs for multiple receipt images (Phoenix version)
 *
 * @param tenant - The tenant identifier
 * @param receiptImageIds - Array of receipt image UUIDs to fetch URLs for
 * @returns Query result with map of image ID to presigned download URL
 *
 * @example
 * ```tsx
 * const { data: imageUrls } = useReceiptImagesPhoenix('test_tenant', [
 *   '550e8400-e29b-41d4-a716-446655440001',
 *   '550e8400-e29b-41d4-a716-446655440002'
 * ]);
 * ```
 */
export const useReceiptImagesPhoenix = (
  tenant: string,
  receiptImageIds?: string[]
): UseQueryResult<
  {
    [key: string]: string | undefined;
  },
  unknown
> => {
  return useApiQuery(
    useServiceProviderReceiptUploadPhoenixApi,
    getReceiptImagesKey(receiptImageIds?.toString()),
    (api) => {
      if (receiptImageIds && receiptImageIds.length > 0) {
        return api.getReceiptUrls({
          tenant,
          receiptIds: receiptImageIds,
        });
      }
      return Promise.resolve({});
    },
    {
      enabled: receiptImageIds !== undefined && receiptImageIds.length !== 0,
      gcTime: 0,
      refetchOnWindowFocus: false,
      structuralSharing: (oldData, newData) =>
        isEqual(oldData, newData)
          ? oldData
          : replaceEqualDeep(oldData, newData),
    }
  );
};

/**
 * Hook that returns a callback function to get a presigned upload URL (Phoenix version)
 *
 * @param tenant - The tenant identifier
 * @returns Callback function that accepts receipt ID, file type, and MD5 hash
 *
 * @example
 * ```tsx
 * const getUploadUrl = useGetUploadUrlPhoenix({ tenant: 'test_tenant' });
 * const url = await getUploadUrl(
 *   '550e8400-e29b-41d4-a716-446655440000',
 *   'image/jpeg',
 *   'abc123=='
 * );
 * ```
 */
export const useGetUploadUrlPhoenix: ({
  tenant,
}: {
  tenant: string;
}) => (
  receiptId: string,
  fileType: string,
  md5Hash: string
) => Promise<string> = ({ tenant }: { tenant: string }) => {
  const api = useServiceProviderReceiptUploadPhoenixApi();
  return useCallback(
    (receiptId: string, fileType: string, md5Hash: string) => {
      return api.getUploadUrl(receiptId, {
        tenant,
        fileType,
        md5Hash,
      });
    },
    [api, tenant]
  );
};

interface UseUploadReceiptImageProps {
  tenant: string;
  uuid: () => string;
  computeMd5Hash: (file: File) => Promise<string>;
}

/**
 * Hook for uploading receipt images to S3 via presigned URLs (Phoenix version)
 *
 * @param tenant - The tenant identifier
 * @param uuid - Function to generate UUIDs for new images
 * @param computeMd5Hash - Function to compute MD5 hash of files
 * @returns Upload function and upload state
 *
 * @example
 * ```tsx
 * const { upload, uploadState } = useUploadReceiptImagePhoenix({
 *   tenant: 'test_tenant',
 *   uuid: () => crypto.randomUUID(),
 *   computeMd5Hash: async (file) => {
 *     // Compute MD5 hash of file
 *     return hashString;
 *   }
 * });
 *
 * // Upload a file
 * const imageId = await upload(file);
 * ```
 */
export const useUploadReceiptImagePhoenix = ({
  tenant,
  uuid,
  computeMd5Hash,
}: UseUploadReceiptImageProps): {
  upload: UploadFile;
  uploadState: UploadState | undefined;
} => {
  const api = useServiceProviderReceiptUploadPhoenixApi();

  const getUploadUrl = useCallback(
    (receiptId: string, fileType: string, md5Hash: string) => {
      return api.getUploadUrl(receiptId, {
        tenant,
        fileType,
        md5Hash,
      });
    },
    [api, tenant]
  );

  const { upload, uploadState } = useUploadFile({
    uuid,
    getUploadUrl,
    computeMd5Hash,
  });

  return {
    upload,
    uploadState,
  };
};

const RECEIPT_IMAGE_URLS_AND_TYPE_KEY = (imageIds?: string[]) => [
  'RECEIPT_IMAGE_URLS_AND_TYPE_PHOENIX_KEY',
  { imageIds },
];

/**
 * Query hook that fetches presigned URLs and determines file types for receipt images (Phoenix version)
 *
 * @param imageIds - Array of image UUIDs
 * @param tenant - The tenant identifier
 * @returns Query result with array of objects containing image ID, URI, and file type
 *
 * @example
 * ```tsx
 * const { data: imagesWithTypes } = useReceiptImageUrlsAndTypePhoenix(
 *   ['550e8400-e29b-41d4-a716-446655440001'],
 *   'test_tenant'
 * );
 * // Returns: [{ imageId: '550e...', imageUri: 'https://...', fileType: 'image/jpeg' }]
 * ```
 */
export const useReceiptImageUrlsAndTypePhoenix = (
  imageIds: string[] | undefined,
  tenant: string
): UseQueryResult<
  Array<{
    imageId: string;
    imageUri?: string;
    fileType?: string;
  }>,
  unknown
> => {
  return useApiQuery(
    useServiceProviderReceiptUploadPhoenixApi,
    RECEIPT_IMAGE_URLS_AND_TYPE_KEY(imageIds),
    async (api) => {
      if (imageIds && imageIds.length > 0) {
        const imageIdsToUris = await api.getReceiptUrls({
          tenant,
          receiptIds: imageIds,
        });

        const imageIdsToTypes = await Promise.all(
          imageIds.map(async (imageId) => {
            const imageUri = imageIdsToUris?.[imageId];
            if (!imageUri) {
              return {
                imageId,
                imageUri: undefined,
                fileType: undefined,
              };
            }

            const response = await fetch(imageUri);
            const extractedFileType =
              response.headers.get('content-type') ?? 'type-unknown';

            return {
              imageId,
              imageUri,
              fileType: extractedFileType,
            };
          })
        );

        return imageIdsToTypes;
      }
      return [];
    }
  );
};
